/***
 *
 *   CATEGORY GENERAL TAB
 *   Phase 6 — reference CategoryGeneralTab chrome: name card, CSV import,
 *   integration connector, danger zone. Keep live ClickUp + CSV APIs.
 *
 **********/

import React, { useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Form, ViewContext, Icon } from 'components/lib';
import integrationsConfig from 'config/integrations';
import { useTranslate } from 'app/translations';
import Style from './ticketsCategory.tailwind';
import { useMutation } from 'react-query';
import { useApiContext } from 'app/api';
import Papa from 'papaparse';

const TicketsCategory = (props) => {
    const { id } = useParams();
    const { _t } = useTranslate();
    const navigate = useNavigate();
    const viewContext = useContext(ViewContext);
    const { callApi } = useApiContext();
    const [csvJSON, setCsvJSON] = useState();
    const [selectedFile, setSelectedFile] = useState();

    const { mutate: importCSV, isLoading: importing } = useMutation(
        () => {
            return callApi({
                url: `/api/tickets/import?category_id=${id}`,
                method: 'POST',
                data: {
                    data: csvJSON,
                },
            });
        },
        {
            onSuccess: (res) => {
                const { data } = res;

                const formatPlural = (count, singular, plural) => {
                    return count <= 1 ? `${count} ${singular}` : `${count} ${plural}`;
                };

                const importedText = `${formatPlural(
                    data.imported,
                    _t('ticket.has'),
                    _t('ticket.have'),
                )} ${_t('import.been')},`;

                const existingText =
                    data.existing > 0
                        ? `${formatPlural(data.existing, 'ticket', 'tickets')} ${_t(
                              'alreadyExist',
                          )},`
                        : '';
                const ignoredText =
                    data.ignored > 0
                        ? `${formatPlural(data.ignored, 'ticket', 'tickets')} ${_t('ignored')},`
                        : '';

                const message = `${importedText} ${existingText} ${ignoredText}`;

                if (res.status === 200) {
                    viewContext.notification.show(message, 'success', true, 'toast');
                }
            },
        },
    );

    const onDelete = () => {
        const projectId = props.data?.project_id;
        viewContext.modal.show(
            {
                title: _t('ticket.category.delete'),
                form: {},
                buttonText: _t('delete'),
                url: `/api/ticket/category/${id}`,
                method: 'delete',
                destructive: true,
            },
            () => {
                if (projectId) {
                    navigate(`/app/settings/projects/${projectId}#ticket-categories`);
                } else {
                    navigate('/app/tickets');
                }
            },
        );
    };

    const integration = props.data?.integration_data;
    const IntegrationForm =
        integrationsConfig[integration?.service]?.ticket?.category?.general?.integration;

    const formData = {
        name: {
            label: _t('ticket.category.name'),
            type: 'text',
            value: props.data?.name,
            required: true,
        },
    };

    if (!props.data?.id && !props.data?.name) {
        return null;
    }

    return (
        <div className={Style.stack}>
            <Card className="w-full" title={_t('ticket.category.info')}>
                <Form
                    data={formData}
                    buttonText={_t('save')}
                    method="PATCH"
                    url={`/api/ticket/category/${id}`}
                    injectedData={{ project_id: props.data?.project_id }}
                />
            </Card>

            <Card className="w-full" title={_t('ticket.category.integration')}>
                <p className={Style.cardHint}>{_t('ticket.category.integrationInfo')}</p>

                {IntegrationForm && (
                    <div className="mb-8">
                        <IntegrationForm
                            data={integration}
                            method="PATCH"
                            url="/api/ticket/category"
                            progressive={false}
                        />
                    </div>
                )}

                <div className={Style.csvBlock}>
                    <div className="mb-3 flex items-center gap-2">
                        <Icon image="upload" size={16} color="currentColor" className="text-text-muted" />
                        <h4 className="text-sm font-semibold text-text-primary">
                            {_t('import.ticket.multiple')} (CSV)
                        </h4>
                    </div>

                    <form className="space-y-3">
                        <input
                            type="file"
                            name="file"
                            id="category-csv-input"
                            hidden
                            accept=".csv"
                            onChange={(e) => {
                                const files = e.target.files;
                                if (!files?.[0]) return;
                                setSelectedFile(files[0].name);
                                Papa.parse(files[0], {
                                    complete: function (results) {
                                        setCsvJSON(results.data);
                                    },
                                });
                            }}
                        />
                        <label htmlFor="category-csv-input" className={Style.fileLabel}>
                            {_t('import.select')}
                        </label>

                        {selectedFile && (
                            <p className="text-sm text-text-secondary">{selectedFile}</p>
                        )}

                        <div className="flex flex-wrap items-center gap-3">
                            <Button
                                type="button"
                                variant="accent"
                                size="sm"
                                disabled={!csvJSON || importing}
                                action={(e) => {
                                    e?.preventDefault();
                                    importCSV();
                                }}
                                text={_t('import.label')}
                            />
                            <a
                                className="text-sm font-medium text-primary-600 hover:underline"
                                href="/assets/example.csv"
                                download>
                                {_t('download.example')}
                            </a>
                        </div>
                    </form>
                </div>
            </Card>

            <Card className={Style.dangerCard} title={_t('ticket.category.delete')}>
                <p className="mb-4 text-sm text-text-secondary">
                    {_t('ticket.category.delete')}
                </p>
                <Button
                    text={_t('delete')}
                    variant="danger"
                    size="sm"
                    action={onDelete}
                />
            </Card>
        </div>
    );
};

export default TicketsCategory;
